import {
  BarChart as ReBarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import { higginsColors } from '@higgins/ui';
import type { ChartRow, ChartFormat } from '@/lib/types';

interface BarChartProps {
  data: ChartRow[];
  index?: string;
  keys?: string[];
  unit?: ChartFormat;
  stacked?: boolean;
}

function fmtTick(v: number, unit: ChartFormat): string {
  if (unit === 'currency') {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
    return `$${v}`;
  }
  if (unit === 'percent') return `${(v * 100).toFixed(0)}%`;
  if (Math.abs(v) >= 1_000) return `${(v / 1_000).toFixed(0)}K`;
  return String(v);
}

function fmtLabel(v: number, unit: ChartFormat): string {
  if (unit === 'currency') {
    const abs = Math.abs(v);
    if (abs >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000)     return `$${(v / 1_000).toFixed(1)}K`;
    return `$${v}`;
  }
  if (unit === 'percent') return `${(v * 100).toFixed(1)}%`;
  return v.toLocaleString();
}

function extractKeyName(key: string): string {
  const m = key.match(/^\[([^\]]+)\]$/);
  const inner = m ? m[1] : key;
  return inner.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim();
}

export function BarChart({ data, index, keys, unit = 'number', stacked = false }: BarChartProps) {
  if (data.length === 0) return null;

  const firstKey = Object.keys(data[0])[0];
  const effectiveIndex = (index && index in data[0]) ? index : firstKey;
  const seriesKeys = keys ?? Object.keys(data[0]).filter(k => k !== effectiveIndex && !k.includes('(Number)'));

  // Format x-axis tick values
  function fmtXTick(v: unknown): string {
    const s = String(v ?? '');
    // YYYYMM integer from 'Date'[Month Year (Number)] → "Apr-25"
    const yyyymm = s.match(/^(\d{4})(\d{2})$/);
    if (yyyymm) {
      const MONTHS_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
      const mo = parseInt(yyyymm[2], 10);
      return `${MONTHS_SHORT[mo - 1] ?? s}-${yyyymm[1].slice(-2)}`;
    }
    // PBI column key like 'Table'[Column] — strip to inner name
    const br = s.match(/\[([^\]]+)\]$/);
    if (br) return br[1].replace(/_/g, ' ');
    // Plain text label — return full value
    return s;
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ReBarChart data={data} margin={{ top: 20, right: 8, bottom: 4, left: 4 }} barCategoryGap="30%">
        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
        <XAxis
          dataKey={effectiveIndex}
          tickFormatter={fmtXTick}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          tickFormatter={v => fmtTick(v as number, unit)}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          axisLine={false}
          tickLine={false}
          width={48}
        />
        <Tooltip
          formatter={(v: unknown) => fmtLabel(v as number, unit)}
          contentStyle={{ fontSize: 12, borderRadius: 6, border: '1px solid #e5e7eb' }}
        />
        {seriesKeys.length > 1 && (
          <Legend wrapperStyle={{ fontSize: 11 }} />
        )}
        {seriesKeys.map((key, i) => {
          // Single-series charts: colour each bar by row index (matches Word export behaviour)
          const perBarColours = seriesKeys.length === 1;
          return (
            <Bar
              key={key}
              dataKey={key}
              name={extractKeyName(key)}
              fill={higginsColors[i % higginsColors.length]}
              stackId={stacked ? 'stack' : undefined}
              radius={[3, 3, 0, 0]}
            >
              {perBarColours && data.map((_, rowIdx) => (
                <Cell key={rowIdx} fill={higginsColors[rowIdx % higginsColors.length]} />
              ))}
              <LabelList
                dataKey={key}
                position="top"
                formatter={(v: unknown) => fmtLabel(v as number, unit)}
                style={{ fontSize: 10, fill: '#374151', fontWeight: 500 }}
              />
            </Bar>
          );
        })}
      </ReBarChart>
    </ResponsiveContainer>
  );
}
